import axios from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CreateDiscountInput,
  DeleteDiscountResponse,
  Discount,
  DiscountIdInput,
  DiscountIndexResponse,
  DiscountIndexSort,
  GenerateDiscountCodeInput,
  GeneratedDiscountCode,
  UpdateDiscountInput,
} from './discounts.types'

export interface DiscountOrganizationScope {
  organizationId: number
}

/** Scope for calls that act on one discount; the Laravel routes require its id. */
export interface DiscountScope extends DiscountOrganizationScope {
  discountId: DiscountIdInput
}

/**
 * The index query key's entries. The scoped key builder always sets the organization; it can't
 * mark the other entries required.
 */
export interface DiscountIndexKeyEntries extends DiscountOrganizationScope {
  /** Matches codes and names. */
  search?: string | undefined
  sort?: DiscountIndexSort | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

/** The show query key's entries; the shared key builder can't mark the discount id required. */
export interface DiscountShowKeyEntries extends DiscountOrganizationScope {
  discountId?: number
}

/** Query functions read only the key (and the abort signal when the caller passes one). */
type DiscountQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

export async function getDiscounts({
  queryKey: [{ organizationId, search = '', sort, page = 1, pageSize = 20 }],
  signal,
}: DiscountQueryContext<DiscountIndexKeyEntries>) {
  const params = {
    page,
    page_size: pageSize,
    ...(search ? { search } : {}),
    ...(sort ? { sort } : {}),
  }

  return (
    (await axios.get<DiscountIndexResponse>(
      '/discounts',
      buildAdminRequestConfig({
        organizationId,
        params,
        ...(signal ? { signal } : {}),
      })
    )) || {
      data: [],
      meta: {
        current_page: page,
        page_size: pageSize,
        total: 0,
        last_page: 1,
      },
    }
  )
}

export async function getDiscount({
  queryKey: [{ organizationId, discountId }],
}: DiscountQueryContext<DiscountShowKeyEntries>) {
  return (
    (await axios.get<Discount>(
      `/discounts/${discountId}`,
      buildAdminRequestConfig({ organizationId })
    )) || null
  )
}

export async function createDiscount({
  organizationId,
  ...values
}: DiscountOrganizationScope & CreateDiscountInput) {
  return axios.post<Discount>('/discounts', values, buildAdminRequestConfig({ organizationId }))
}

export async function updateDiscount({
  organizationId,
  discountId,
  ...values
}: DiscountScope & UpdateDiscountInput) {
  return axios.put<Discount>(
    `/discounts/${discountId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function deleteDiscount({ organizationId, discountId }: DiscountScope) {
  return axios.delete<DeleteDiscountResponse>(
    `/discounts/${discountId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function generateDiscountCode({
  organizationId,
  name,
}: DiscountOrganizationScope & GenerateDiscountCodeInput) {
  return axios.post<GeneratedDiscountCode>(
    '/discounts/generate-code',
    { name },
    buildAdminRequestConfig({ organizationId })
  )
}
